import { useEffect, useId, useRef, useState } from "react";
import {
  GeocodingError,
  nominatimGeocodingProvider,
  type GeocodeResult,
  type GeocodingProvider,
  type MapCoordinates,
} from "@vimos/api-client";
import { Button } from "./Button";
import { Input } from "./Input";

import "leaflet/dist/leaflet.css";

type LeafletModule = typeof import("leaflet");
type LeafletMap = import("leaflet").Map;
type LeafletMarker = import("leaflet").Marker;
type LeafletMouseEvent = import("leaflet").LeafletMouseEvent;

/** Centro por defecto: Peru, para que el mapa siempre tenga contexto. */
const PERU_CENTER: MapCoordinates = { latitude: -9.19, longitude: -75.0152 };

const PIN_HTML =
  '<span class="block h-5 w-5 rounded-full border-[3px] border-white bg-[#C9A24D] shadow-lg ring-2 ring-[#0B132B]"></span>';

export interface LocationPickerLabels {
  legend: string;
  searchLabel: string;
  searchPlaceholder: string;
  searchButton: string;
  searching: string;
  noResults: string;
  minQuery: string;
  mapLabel: string;
  useMapCenter: string;
  emptyHint: string;
  pendingTitle: string;
  pendingHint: string;
  confirm: string;
  discard: string;
  confirmedBadge: string;
  confirmedTitle: string;
  confirmedHint: string;
  modify: string;
  clear: string;
  mapUnavailable: string;
  attribution: string;
  usagePolicy: string;
}

const DEFAULT_LABELS: LocationPickerLabels = {
  legend: "Ubicación exacta del negocio",
  searchLabel: "Busca la dirección que escribiste arriba",
  searchPlaceholder: "Calle, avenida y número; puedes añadir tu distrito",
  searchButton: "Buscar esta dirección",
  searching: "Buscando direcciones…",
  noResults:
    "No encontramos esa dirección. Añade el distrito y la provincia al campo Dirección. También puedes acercarte con + y − y tocar el mapa donde está tu negocio.",
  minQuery: "Escribe al menos 3 caracteres para buscar.",
  mapLabel: "Mapa para seleccionar la ubicación",
  useMapCenter: "Marcar el punto al centro del mapa",
  emptyHint:
    "Busca la dirección o toca el mapa sobre tu local. Cuando veas el pin en el lugar correcto, confirma para guardarlo.",
  pendingTitle: "¿El pin está sobre tu negocio?",
  pendingHint: "Puedes moverlo tocando otra parte del mapa. Confirma cuando quede en el lugar correcto.",
  confirm: "Sí, usar esta ubicación",
  discard: "Volver al mapa",
  confirmedBadge: "Confirmada",
  confirmedTitle: "Ubicación guardada",
  confirmedHint: "Este punto aparecerá en el mapa de tu ficha.",
  modify: "Cambiar ubicación",
  clear: "Quitar punto del mapa",
  mapUnavailable:
    "No pudimos cargar el mapa. Aún puedes buscar tu dirección y confirmar el punto desde los resultados.",
  attribution: "Búsqueda de direcciones por OpenStreetMap (Nominatim)",
  usagePolicy: "Política de uso",
};

export interface LocationPickerProps {
  /** Coordenadas confirmadas (null = sin ubicación). */
  value: MapCoordinates | null;
  /** Notifica cambios confirmados (o null al quitar la ubicación). */
  onChange: (value: MapCoordinates | null) => void;
  /** Texto que se precarga en el buscador (no dispara ninguna búsqueda). */
  addressHint?: string | undefined;
  /** Completa la dirección cuando se confirma un resultado del mapa. */
  onAddressResolved?: ((address: string) => void) | undefined;
  disabled?: boolean | undefined;
  /** Centro inicial del mapa cuando aún no hay coordenadas. */
  defaultCenter?: MapCoordinates | undefined;
  /** Zoom inicial cuando aún no hay coordenadas (por defecto 6). */
  defaultZoom?: number | undefined;
  /** Máximo de resultados mostrados (1..10, por defecto 5). */
  maxResults?: number | undefined;
  /** Proveedor de geocodificación sustituible. */
  provider?: GeocodingProvider | undefined;
  labels?: Partial<LocationPickerLabels> | undefined;
  className?: string | undefined;
}

interface PendingPoint {
  point: MapCoordinates;
  label: string | null;
  address: string | null;
}

function samePoint(a: MapCoordinates | null, b: MapCoordinates | null): boolean {
  if (!a || !b) return a === b;
  return a.latitude === b.latitude && a.longitude === b.longitude;
}

function isAbort(error: unknown): boolean {
  return error instanceof GeocodingError && error.code === "aborted";
}

function errorMessage(error: unknown): string {
  if (error instanceof GeocodingError) {
    if (error.code === "rate_limited") return error.message;
    if (error.code === "unavailable") return error.message;
    if (error.code === "network") return error.message;
    if (error.code === "invalid_query") return error.message;
  }
  return "No pudimos buscar la dirección. Revisa tu conexión e inténtalo de nuevo.";
}

export function LocationPicker({
  value,
  onChange,
  addressHint,
  onAddressResolved,
  disabled = false,
  defaultCenter,
  defaultZoom,
  maxResults = 5,
  provider = nominatimGeocodingProvider,
  labels,
  className = "",
}: LocationPickerProps) {
  const text = { ...DEFAULT_LABELS, ...labels };
  const searchId = useId();

  const [searchTerm, setSearchTerm] = useState(() => (addressHint ?? "").trim());
  const [results, setResults] = useState<GeocodeResult[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingPoint | null>(null);
  const [confirmedLabel, setConfirmedLabel] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<LeafletMarker | null>(null);
  const leafletRef = useRef<LeafletModule | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const previousAddressHintRef = useRef((addressHint ?? "").trim());
  const centerOnRef = useRef<((point: MapCoordinates, zoom?: number) => void) | null>(null);
  const prevValueRef = useRef<MapCoordinates | null>(null);

  useEffect(() => {
    const nextHint = (addressHint ?? "").trim();
    if (nextHint !== previousAddressHintRef.current) {
      // Un candidato elegido deja de ser válido si el cliente cambia la
      // dirección mientras todavía no confirma el pin.
      setPending(null);
      previousAddressHintRef.current = nextHint;
    }
    setSearchTerm(nextHint);
    setResults(null);
    setSearchError(null);
  }, [addressHint]);

  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const providerRef = useRef(provider);
  providerRef.current = provider;

  const initialRef = useRef({
    center: value ?? defaultCenter ?? PERU_CENTER,
    zoom: value ? 16 : (defaultZoom ?? 6),
  });

  const markerPosition: MapCoordinates | null = pending?.point ?? value;
  const isConfirmed = value != null && pending === null;

  useEffect(() => {
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    let onResize: (() => void) | null = null;

    (async () => {
      try {
        const imported = await import("leaflet");
        if (cancelled || !containerRef.current) return;
        const L = ((imported as { default?: LeafletModule }).default ?? imported) as LeafletModule;
        leafletRef.current = L;

        const start = initialRef.current.center;
        const map = L.map(containerRef.current, {
          center: [start.latitude, start.longitude],
          zoom: initialRef.current.zoom,
          zoomControl: true,
          keyboard: true,
          scrollWheelZoom: true,
          attributionControl: true,
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
        }).addTo(map);

        map.on("click", (event: LeafletMouseEvent) => {
          if (disabledRef.current) return;
          setPending({
            point: { latitude: event.latlng.lat, longitude: event.latlng.lng },
            label: null,
            address: null,
          });
        });

        centerOnRef.current = (point, zoom) => {
          map.flyTo([point.latitude, point.longitude], zoom ?? 16, { duration: 0.6 });
        };

        mapRef.current = map;
        setMapFailed(false);
        setReady(true);

        // El contenedor cambia de tamaño al hidratar, al rotar el móvil o al
        // abrirse el teclado en pantalla: Leaflet debe recalcular sus mosaicos.
        requestAnimationFrame(() => map.invalidateSize());
        onResize = () => map.invalidateSize();
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => map.invalidateSize());
          resizeObserver.observe(containerRef.current);
        }
      } catch {
        if (!cancelled) {
          setReady(false);
          setMapFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (onResize) {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("orientationchange", onResize);
      }
      resizeObserver?.disconnect();
      resizeObserver = null;
      centerOnRef.current = null;
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
      leafletRef.current = null;
    };
  }, []);

  useEffect(() => {
    const L = leafletRef.current;
    const map = mapRef.current;
    if (!ready || !L || !map) return;

    if (!markerPosition) {
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      return;
    }

    const latlng: [number, number] = [markerPosition.latitude, markerPosition.longitude];

    if (!markerRef.current) {
      const marker = L.marker(latlng, {
        draggable: !disabled,
        keyboard: true,
        title: "Punto de ubicación",
        icon: L.divIcon({
          className: "vimos-location-pin",
          html: PIN_HTML,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
      });
      marker.on("dragend", () => {
        const position = marker.getLatLng();
        setPending((previous) => ({
          point: { latitude: position.lat, longitude: position.lng },
          label: previous?.label ?? null,
          address: previous?.address ?? null,
        }));
      });
      marker.addTo(map);
      markerRef.current = marker;
    } else {
      markerRef.current.setLatLng(latlng);
    }
  }, [ready, markerPosition, disabled]);

  useEffect(() => {
    if (markerRef.current && !pending) {
      if (disabled) markerRef.current.dragging?.disable();
      else markerRef.current.dragging?.enable();
    }
  }, [disabled, pending]);

  useEffect(() => {
    if (value && !samePoint(value, prevValueRef.current)) {
      centerOnRef.current?.(value, 15);
    }
    prevValueRef.current = value;
  }, [value]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function runSearch() {
    if (disabled) return;
    const query = searchTerm.trim();
    if (query.length < 3) {
      setResults([]);
      setSearchError(text.minQuery);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsSearching(true);
    setSearchError(null);
    setResults(null);
    try {
      const found = await providerRef.current.search(query, {
        limit: maxResults,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setResults(found);
    } catch (error) {
      if (!isAbort(error)) setSearchError(errorMessage(error));
    } finally {
      if (!controller.signal.aborted) setIsSearching(false);
    }
  }

  function chooseResult(result: GeocodeResult) {
    const point = { latitude: result.latitude, longitude: result.longitude };
    setPending({
      point,
      label: result.label || result.name || null,
      address: [result.name, result.address].filter(Boolean).join(", ") || result.label || null,
    });
    centerOnRef.current?.(point, 16);
  }

  function placeAtCenter() {
    const map = mapRef.current;
    if (!map || disabled) return;
    const center = map.getCenter();
    setPending({ point: { latitude: center.lat, longitude: center.lng }, label: null, address: null });
  }

  function confirm() {
    if (!pending) return;
    setConfirmedLabel(pending.label);
    if (pending.address) onAddressResolved?.(pending.address);
    onChange(pending.point);
    setPending(null);
  }

  function startEditing() {
    if (!value) return;
    setPending({ point: value, label: confirmedLabel, address: null });
    centerOnRef.current?.(value, 16);
  }

  function clear() {
    setPending(null);
    setConfirmedLabel(null);
    setResults(null);
    setSearchError(null);
    onChange(null);
  }

  return (
    <fieldset className={`min-w-0 ${className}`} disabled={disabled}>
      <legend className="font-body text-ink text-sm font-medium">{text.legend}</legend>
      {isConfirmed ? (
        <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 align-middle text-xs font-medium text-emerald-800">
          {text.confirmedBadge}
        </span>
      ) : null}

      <div role="search" className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Input
            id={searchId}
            label={text.searchLabel}
            value={searchTerm}
            placeholder={text.searchPlaceholder}
            autoComplete="off"
            disabled={disabled || isSearching}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setResults(null);
              setSearchError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void runSearch();
              }
            }}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          isLoading={isSearching}
          disabled={disabled}
          onClick={() => void runSearch()}
        >
          {text.searchButton}
        </Button>
      </div>

      {isSearching ? (
        <p role="status" className="text-mist mt-2 text-sm">
          {text.searching}
        </p>
      ) : null}
      {searchError ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {searchError}
        </p>
      ) : null}
      {results && results.length === 0 && !isSearching && !searchError ? (
        <div className="border-sand/50 bg-sand/10 text-ink mt-2 rounded-md border px-3 py-2 text-sm">
          {text.noResults}
        </div>
      ) : null}
      {results && results.length > 0 ? (
        <div className="mt-2 overflow-hidden rounded-md border border-blue-200 bg-blue-50/60">
          <p className="px-3 py-2 text-xs font-semibold text-blue-900">
            Elige el resultado que coincide con tu negocio:
          </p>
          <ul className="border-sand/50 divide-sand/40 max-h-56 divide-y overflow-y-auto border-t bg-white">
            {results.map((result, index) => (
              <li key={result.id}>
                <button
                  type="button"
                  onClick={() => chooseResult(result)}
                  className="focus-visible:ring-gold hover:bg-sand/20 w-full px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                >
                  <span className="text-ink block text-sm font-medium">
                    <span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] text-blue-800">
                      {index + 1}
                    </span>
                    {result.name || result.label || "Dirección"}
                  </span>
                  <span className="text-mist block text-xs">
                    {[result.address, result.locality].filter(Boolean).join(", ") || result.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {mapFailed ? (
        <div
          role="alert"
          className="border-gold/40 bg-gold/10 text-navy mt-3 rounded-md border px-3 py-2 text-sm"
        >
          {text.mapUnavailable}
        </div>
      ) : (
        <div className="border-sand/60 bg-sand/10 relative isolate z-0 mt-3 overflow-hidden rounded-xl border shadow-sm">
          <div
            ref={containerRef}
            role="application"
            aria-label={text.mapLabel}
            className="h-64 w-full sm:h-80 lg:h-96"
            aria-busy={!ready}
          />
          {!ready ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/85 px-4 text-center text-sm font-medium text-slate-600">
              Preparando el mapa…
            </div>
          ) : null}
        </div>
      )}

      {!mapFailed && ready ? (
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Acércate con los controles <span className="font-semibold">+ / −</span> y toca el mapa sobre tu
          local. También puedes arrastrar el pin.
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="ghost" onClick={placeAtCenter} disabled={disabled || mapFailed}>
          {text.useMapCenter}
        </Button>
        <p className="text-mist text-xs">
          {text.attribution}.{" "}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            OpenStreetMap
          </a>
          {" · "}
          <a
            href="https://operations.osmfoundation.org/policies/nominatim/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {text.usagePolicy}
          </a>
        </p>
      </div>

      {isConfirmed ? (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-sm font-medium text-emerald-800">{text.confirmedTitle}</p>
          <p className="mt-0.5 text-xs text-emerald-700">{confirmedLabel ?? text.confirmedHint}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={startEditing} disabled={disabled}>
              {text.modify}
            </Button>
            <Button type="button" variant="ghost" onClick={clear} disabled={disabled}>
              {text.clear}
            </Button>
          </div>
        </div>
      ) : pending ? (
        <div className="border-gold/50 bg-gold/10 mt-3 rounded-md border p-3">
          <p className="text-ink text-sm font-medium">{text.pendingTitle}</p>
          <p className="text-mist mt-0.5 text-xs">{pending.label ?? text.pendingHint}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button type="button" onClick={confirm} disabled={disabled}>
              {text.confirm}
            </Button>
            <Button type="button" variant="ghost" onClick={clear} disabled={disabled}>
              {text.discard}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-mist mt-3 text-sm">{text.emptyHint}</p>
      )}
    </fieldset>
  );
}
